import "./styles.css";
import "../../Customer/homepage/styles.css";
import "../../Css/animation.css";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactPixel from "react-facebook-pixel";
import TagManager from "react-gtm-module";
import { useQuery, useMutation } from "@tanstack/react-query";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import {
    kirkos_logo_white_yellow,
    car_home,
    home_backdrop,
    rental_car,
    location_icon,
    icons_armour,
    car_key,
    mileage,
    petrol_pump,
    group_icon,
    noun_no_image,
    dashboard_special_deal,
} from "../../../assets/images";
import { trackEvent } from "../../../services/events";
import SEO from "../../../components/SEO";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";
import {
    StarFill,
    PencilSquare,
    Airplane,
    Calendar3,
    CarFront,
    Check2,
    ChevronLeft,
    ChevronRight,
    Clock,
    Funnel,
    GeoAlt,
    Palette,
    Search,
    ShieldCheck,
    Tools,
    ArrowClockwise,
    TagFill,
} from "react-bootstrap-icons";
import { Modal, Offcanvas } from "react-bootstrap";
import FloatingInputLocation from "../../../components/FloatingInputLocation";
import DateTimePickerComponent from "../../../components/DateTimePickerComponent";
import CustomerCustomizeYourSearch from "../../../components/CustomerDashBoard/CustomerCustomizeYourSearch";
import QuickSearchModal from "../../../components/QuickSearchModal";
import LocationPickerModal from "../../../components/LocationPickerModal";
import DraggableWhatsAppIcon from "../../../components/CustomerDashBoard/DraggableWhatsAppIcon";
import { initCapAllWords } from "../../../utils/stringUtil";
import { ProductSearch, searchResult } from "../../../types";

interface Location {
    latitude: number | null;
    longitude: number | null;
}

interface FieldErrors {
    pickupDate?: string;
    dropOffDate?: string;
    pickupTime?: string;
    dropOffTime?: string;
    pickupLocation?: string;
    returnLocation?: string;
}

const WHATSAPP_NUMBER = "+97165674835";

const highlights = [
    {
        title: "Instant booking",
        description: "Reserve in minutes with clear pricing and zero surprises.",
        icon: car_key,
    },
    {
        title: "UAE-wide delivery",
        description: "Pick up at airport, hotel, or your doorstep.",
        icon: location_icon,
    },
    {
        title: "Fully insured",
        description: "Drive with confidence with comprehensive cover.",
        icon: icons_armour,
    },
    {
        title: "Curated fleet",
        description: "Sedans, SUVs, and premium models for every plan.",
        icon: rental_car,
    },
];

const featureTiles = [
    {
        title: "Unlimited mileage options",
        value: "Drive freely across Emirates",
        icon: mileage,
    },
    {
        title: "Fuel-friendly choices",
        value: "Efficient cars with modern engines",
        icon: petrol_pump,
    },
    {
        title: "Spacious interiors",
        value: "Family-ready seating and comfort",
        icon: group_icon,
    },
];

const steps = [
    {
        title: "Choose your ride",
        description:
            "Select pickup, return, and the car category that fits your trip.",
    },
    {
        title: "Confirm in seconds",
        description:
            "Transparent pricing, clear terms, and instant reservation updates.",
    },
    {
        title: "Drive with ease",
        description:
            "Pick up or get delivery and enjoy a smooth, supported rental.",
    },
];

const metaStats = [
    { label: "Rated by renters", value: "4.8/5" },
    { label: "Active cars", value: "1,500+" },
    { label: "Support coverage", value: "24/7" },
];

const cityTags = [
    "Dubai",
    "Sharjah",
];

const KirkosMetaLanding = () => {
    const navigate = useNavigate();
    const { "campaign_id": campaignIdParam } = useParams();
    const [searchParams] = useSearchParams();
    const campaignIdQuery = searchParams.get("campaign_id") || searchParams.get("campaignId");
    const campaignCode = campaignIdParam || campaignIdQuery;

    // Fetch fallback campaign key from app config API if campaign code is not present in URL
    const { data: appConfigCampaignKey } = useQuery({
        queryKey: ["appConfigCampaignKey"],
        queryFn: async () => {
            const response = await axiosInstance.get(
                "carhire-customer-merchant/customer/service/app-config/CAMPAIGN_KEY"
            );
            return response.data; // e.g. { "key": "CAMPAIGN_KEY", "value": "CODE00123" }
        },
        enabled: !campaignCode,
    });

    const finalCampaignCode = campaignCode || appConfigCampaignKey?.value;

    // Extract UTM and campaign parameters from URL, falling back to fetched CAMPAIGN_KEY if missing in URL
    const utmParams = useMemo(() => {
        return {
            utm_source: searchParams.get("utm_source") || null,
            utm_medium: searchParams.get("utm_medium") || null,
            utm_campaign: searchParams.get("utm_campaign") || null,
            utm_content: searchParams.get("utm_content") || null,
            utm_term: searchParams.get("utm_term") || null,
            placement: searchParams.get("placement") || null,
            campaign_id: searchParams.get("campaign_id") || finalCampaignCode || null,
            adset_id: searchParams.get("adset_id") || null,
            ad_id: searchParams.get("ad_id") || null,
        };
    }, [searchParams, finalCampaignCode]);

    // Filter out null values for clean tracking
    const cleanUtmParams = useMemo(() => {
        return Object.fromEntries(
            Object.entries(utmParams).filter(([_, value]) => value !== null)
        );
    }, [utmParams]);

    // Save UTM params to localStorage whenever they change
    useEffect(() => {
        if (Object.keys(cleanUtmParams).length > 0) {
            localStorage.setItem("kirkos_meta_utm_params", JSON.stringify(cleanUtmParams));
        }
    }, [cleanUtmParams]);

    const searchFormRef = useRef<HTMLDivElement>(null);
    const resultsShellRef = useRef<HTMLDivElement>(null);

    // --- Search States ---
    const [searchResultHeading, setSearchResultHeading] = useState(false);
    const [locationResolved, setLocationResolved] = useState<boolean>(false);
    const [pickupLocation, setPickupLocation] = useState<Location>({ latitude: null, longitude: null });
    const [returnLocation, setReturnLocation] = useState<Location>({ latitude: null, longitude: null });

    const [pickupDate, setPickupDate] = useState<any>(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow;
    });

    const [dropOffDate, setDropOffDate] = useState<any>(() => {
        const d = new Date();
        d.setDate(d.getDate() + 8); // default to one week after today
        return d;
    });

    const [selectedTime, setSelectedTime] = useState("10:00");
    const [selectedDropTime, setSelectedDropTime] = useState("10:00");

    const [searchValues, setSearchValues] = useState<any>({
        pickup: "",
        return: "",
    });

    const [searchCars, SetSearchCars] = useState<any>([]);
    const [searchMessage, setSearchMessage] = useState<string | null>(null);
    const [isSameDate, setIsSameDate] = useState<boolean>(false);
    const [isMobileView, setIsMobileView] = useState(false);
    const [brandSearchQuery, setBrandSearchQuery] = useState("");
    const [selectedCarTypes, setSelectedCarTypes] = useState<string[]>([]);
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [selectedFuelTypes, setSelectedFuelTypes] = useState<string[]>([]);
    const [selectedTransmissions, setSelectedTransmissions] = useState<string[]>([]);
    const [isUnlimitedMileage, setIsUnlimitedMileage] = useState(false);
    const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
    const [weeklyPriceRange, setWeeklyPriceRange] = useState({ min: 0, max: 0 });
    const [monthlyPriceRange, setMonthlyPriceRange] = useState({ min: 0, max: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;
    const [pickupTimeError, setPickupTimeError] = useState<string>("");
    const [dropOffTimeError, setDropOffTimeError] = useState<string>("");
    const [activeFilter, setActiveFilter] = useState<string | null>(null);
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);
    const [customizeResult, setCustomizeResult] = useState<any>(null);
    const [isRotating, setIsRotating] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    // Modals
    const [showCreateProduct, setShowCreateProduct] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [showPickupMapModal, setShowPickupMapModal] = useState(false);
    const [showReturnMapModal, setShowReturnMapModal] = useState(false);
    const [showAcceptConfirmationModal, setShowAcceptConfirmationModal] = useState(false);
    const [selectedBookingItem, setSelectedBookingItem] = useState<any>(null);
    const [acceptConfirmationMessage, setAcceptConfirmationMessage] = useState("");

    const [returnToSameLocation, setReturnToSameLocation] = useState(true);
    const [pickupSuggestions, setPickupSuggestions] = useState<{ name: string; latitude: number; longitude: number }[]>([]);
    const [returnSuggestions, setReturnSuggestions] = useState<{ name: string; latitude: number; longitude: number }[]>([]);

    const { control } = useForm({
        mode: "onChange",
    });

    // Detect mobile webview / responsive layout
    useEffect(() => {
        const userAgent = navigator.userAgent.toLowerCase();
        const webViewKeywords = ["wv", "version/"];
        const isAndroidWebView =
            /android/.test(userAgent) &&
            webViewKeywords.some((kw) => userAgent.includes(kw));
        const isIosWebView =
            /iphone | ipod | ipad /.test(userAgent) &&
            (userAgent.includes("safari") === false ||
                /crios | fxios/.test(userAgent));

        setIsMobileView(isAndroidWebView || isIosWebView);
    }, []);

    // Facebook Pixel initialization
    useEffect(() => {
        if (process.env.REACT_APP_META_PIXEL_ID && finalCampaignCode) {
            try {
                ReactPixel.init(process.env.REACT_APP_META_PIXEL_ID);
                ReactPixel.track('ViewContent', {
                    content_type: 'Kirkos Meta Landing Page',
                    content_name: 'Kirkos Meta Landing Page',
                    ...cleanUtmParams,
                });
                console.log('Facebook Pixel initialized successfully');
            } catch (error) {
                console.error('Facebook Pixel initialization failed:', error);
            }
        }
    }, [cleanUtmParams, finalCampaignCode]);

    const pageLoadTracked = useRef(false);
    useEffect(() => {
        if (finalCampaignCode && !pageLoadTracked.current) {
            pageLoadTracked.current = true;
            trackEvent({
                event_name: {
                    web: "Kirkos_Meta_Page_Load",
                    app: "Kirkos_Meta_Page_Load_App",
                },
                event_category: "Page_Load",
                module: "Marketing",
                payload: { ...cleanUtmParams },
            }).catch(() => { });
        }
    }, [finalCampaignCode, cleanUtmParams]);

    // Current geolocation lookup disabled on landing campaign page to respect campaign API values exclusively

    // Handle outside clicks for filter combo boxes
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (activeFilter) {
                const target = event.target as HTMLElement;
                if (!target.closest(".home-results-combo")) {
                    setActiveFilter(null);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [activeFilter]);

    // Load assets from React-Query
    const { data: marketingAssets } = useQuery({
        queryKey: ["marketingAssets", "LUXURY8"],
        queryFn: async () => {
            const response = await axiosInstance.get(API_URL.MARKETING_ASSETS, {
                params: {
                    active: true,
                },
                headers: {
                    location: "TVM",
                    language: "English",
                },
            });
            return response.data;
        },
    });

    const luxuryAsset = marketingAssets?.find(
        (asset: any) => asset.active && asset.type === "LUXURY"
    );
    const claimAsset = marketingAssets?.find(
        (asset: any) => asset.active && asset.type === "CLAIM"
    );

    // Load Campaign Data from URL Campaign-ID or App Config fallback
    const { data: campaignData } = useQuery({
        queryKey: ["campaignData", finalCampaignCode],
        queryFn: async () => {
            if (!finalCampaignCode) return null;
            const response = await axiosInstance.get(
                `${API_URL.CAMPAIGN_CODE}/${finalCampaignCode}`
            );
            return response.data;
        },
        enabled: !!finalCampaignCode,
    });

    // Synchronize pickup location from campaign get API
    useEffect(() => {
        if (campaignData?.pickup_location) {
            const loc = campaignData.pickup_location;
            const address = loc.address_1 || "";
            const lat = loc.latitude ? Number(loc.latitude) : null;
            const lng = loc.longitude ? Number(loc.longitude) : null;

            setSearchValues((prev: any) => ({
                ...prev,
                pickup: address,
                ...(returnToSameLocation ? { return: address } : {})
            }));
            setPickupLocation({ latitude: lat, longitude: lng });
            if (returnToSameLocation) {
                setReturnLocation({ latitude: lat, longitude: lng });
            }
            setLocationResolved(true);
        }
    }, [campaignData, returnToSameLocation]);

    // Auto-load campaign products once locations are resolved from the Campaign GET API
    useEffect(() => {
        if (
            pickupLocation.latitude !== null &&
            pickupLocation.longitude !== null &&
            returnLocation.latitude !== null &&
            returnLocation.longitude !== null &&
            !searchResultHeading
        ) {
            handleSubmit(true);
        }
    }, [pickupLocation, returnLocation, searchResultHeading]);

    // --- Time validations ---
    const validateTime = (time: string): boolean => {
        const [hours, minutes] = time.split(':').map(Number);
        return (hours === 22 && minutes === 0) || (hours >= 9 && hours < 22);
    };

    useEffect(() => {
        if (!pickupDate) {
            setPickupTimeError("");
            return;
        }

        const timeToValidate = selectedTime || "09:00";
        setFieldErrors(prev => ({ ...prev, pickupTime: undefined }));
        const selectedDate = new Date(pickupDate);
        const today = new Date();
        const currentHour = today.getHours();
        const currentMinutes = today.getMinutes();
        const minimumTime = new Date(today.getTime() + 2 * 60 * 60 * 1000);
        const [hours, minutes] = timeToValidate.split(":").map(Number);
        const isToday =
            selectedDate.getDate() === today.getDate() &&
            selectedDate.getMonth() === today.getMonth() &&
            selectedDate.getFullYear() === today.getFullYear();

        const isNoServiceToday = isToday && (currentHour * 60 + currentMinutes + 120 >= 22 * 60);

        if (isNoServiceToday) {
            setPickupTimeError("* No service available for today. Please select tomorrow's date");
        } else if (isToday) {
            const currentTotalMinutes = currentHour * 60 + currentMinutes;
            const selectedTotalMinutes = hours * 60 + minutes;
            const minimumTotalMinutes = currentTotalMinutes + 120;

            if (minimumTotalMinutes >= 22 * 60) {
                setPickupTimeError("* No service available for today.");
            } else if (
                selectedTotalMinutes < minimumTotalMinutes ||
                !((hours < 22) || (hours === 22 && minutes === 0))
            ) {
                const minHour = minimumTime.getHours();
                const minMinute = minimumTime.getMinutes();
                setPickupTimeError(
                    `* Today services are available between ${String(minHour).padStart(2, "0")}:${String(minMinute).padStart(2, "0")} to 22:00`
                );
            } else {
                setPickupTimeError("");
            }
        } else {
            if (!((hours === 22 && minutes === 0) || (hours >= 9 && hours < 22))) {
                setPickupTimeError("* Services available between 09:00 to 22:00");
            } else {
                setPickupTimeError("");
            }
        }
    }, [pickupDate, selectedTime]);

    useEffect(() => {
        if (!pickupDate || !dropOffDate || !selectedTime || !selectedDropTime) {
            setIsSameDate(false);
            return;
        }

        try {
            const pDate = new Date(pickupDate);
            const dDate = new Date(dropOffDate);

            if (Number.isNaN(pDate.getTime()) || Number.isNaN(dDate.getTime())) {
                setIsSameDate(false);
                return;
            }

            const pDateStr = pDate.toISOString().split("T")[0];
            const dDateStr = dDate.toISOString().split("T")[0];

            if (pDateStr > dDateStr) {
                setIsSameDate(true);
            } else if (pDateStr === dDateStr) {
                const [pH, pM] = selectedTime.split(":").map(Number);
                const [dH, dM] = selectedDropTime.split(":").map(Number);
                const pTotal = pH * 60 + pM;
                const dTotal = dH * 60 + dM;

                setIsSameDate(dTotal <= pTotal);
            } else {
                setIsSameDate(false);
            }
        } catch (e) {
            setIsSameDate(false);
        }
    }, [pickupDate, dropOffDate, selectedTime, selectedDropTime]);

    // --- Form changes & map select ---
    const handlePickupDateChange = (date: string | null) => {
        if (!date) {
            setPickupDate("");
            return;
        }
        trackEvent({ event_name: "Kirkos_Meta_Pickup_Date_Selected", event_category: "Search_Interaction", module: "Marketing", payload: { pickup_date: date } }).catch(() => { });
        setPickupDate(date);
        setFieldErrors(prev => ({ ...prev, pickupDate: undefined }));
    };

    const handleDropOffDateChange = (date: string | null) => {
        if (!date) {
            setDropOffDate("");
            return;
        }
        trackEvent({ event_name: "Kirkos_Meta_DropOff_Date_Selected", event_category: "Search_Interaction", module: "Marketing", payload: { dropoff_date: date } }).catch(() => { });
        setDropOffDate(date);

        const selectedDate = new Date(date);
        if (pickupDate) {
            const maxAllowed = new Date(pickupDate);
            maxAllowed.setDate(maxAllowed.getDate() + 30);

            if (selectedDate > maxAllowed) {
                setFieldErrors(prev => ({
                    ...prev,
                    dropOffDate: "Drop-off date cannot be more than 30 days from the pickup date",
                }));
            } else {
                setFieldErrors(prev => ({ ...prev, dropOffDate: undefined }));
            }
        } else {
            setFieldErrors(prev => ({ ...prev, dropOffDate: undefined }));
        }
    };

    const handleDropOffTimeChange = (newTime: string | null) => {
        if (newTime) {
            setSelectedDropTime(newTime);
            setFieldErrors(prev => ({ ...prev, dropOffTime: undefined }));
            if (!validateTime(newTime)) {
                setDropOffTimeError("* Services available between 09:00 to 22:00");
            } else {
                setDropOffTimeError("");
            }
        } else {
            setDropOffTimeError("");
        }
    };

    const handleTimeChange = (time: string | null) => {
        if (time) {
            setSelectedTime(time);
            setFieldErrors(prev => ({ ...prev, pickupTime: undefined }));
        } else {
            setPickupTimeError("");
        }
    };

    const fetchCoordinates = async (searchQuery: string) => {
        try {
            const response = await axiosInstance.get(
                `https://nominatim.kirkos.ae/search.php?q=${searchQuery}&format=geocodejson`
            );
            if (response.data && response.data.features.length > 0) {
                const suggestions = response.data.features.map((feature: any) => ({
                    name: feature.properties.geocoding.label,
                    latitude: feature.geometry.coordinates[1],
                    longitude: feature.geometry.coordinates[0],
                }));
                return suggestions;
            }
            return [];
        } catch (error) {
            return [];
        }
    };

    const handlePickupLocationChange = async (value: string) => {
        setSearchValues((prevValues: any) => ({ ...prevValues, pickup: value }));
        try {
            const suggestions = await fetchCoordinates(value);
            if (suggestions.length > 0) {
                const { latitude, longitude } = suggestions[0];
                setPickupSuggestions(suggestions);
                setPickupLocation({ latitude, longitude });
            } else {
                setPickupSuggestions([]);
                setPickupLocation({ latitude: null, longitude: null });
            }
        } catch (error) {
            console.error("Error fetching pickup location suggestions:", error);
        }
    };

    const handleReturnLocationChange = async (value: string) => {
        setSearchValues((prevValues: any) => ({ ...prevValues, return: value }));
        try {
            const suggestions = await fetchCoordinates(value);
            if (suggestions.length > 0) {
                const { latitude, longitude } = suggestions[0];
                setReturnSuggestions(suggestions);
                setReturnLocation({ latitude, longitude });
            } else {
                setReturnSuggestions([]);
                setReturnLocation({ latitude: null, longitude: null });
            }
        } catch (error) {
            console.error("Error fetching return location suggestions:", error);
        }
    };

    const handlePickupMapSelect = (location: { name: string; latitude: number; longitude: number }) => {
        setLocationResolved(true);
        setSearchValues((prevValues: any) => ({
            ...prevValues,
            pickup: location.name,
        }));
        setPickupLocation({
            latitude: location.latitude,
            longitude: location.longitude,
        });
        setFieldErrors(prev => ({ ...prev, pickupLocation: undefined }));
        setPickupSuggestions([]);

        if (returnToSameLocation) {
            setSearchValues((prevValues: any) => ({
                ...prevValues,
                return: location.name,
            }));
            setReturnLocation({
                latitude: location.latitude,
                longitude: location.longitude,
            });
            setFieldErrors(prev => ({ ...prev, returnLocation: undefined }));
        }
    };

    const handleReturnMapSelect = (location: { name: string; latitude: number; longitude: number }) => {
        setLocationResolved(true);
        setSearchValues((prevValues: any) => ({
            ...prevValues,
            return: location.name,
        }));
        setReturnLocation({
            latitude: location.latitude,
            longitude: location.longitude,
        });
        setFieldErrors(prev => ({ ...prev, returnLocation: undefined }));
        setReturnSuggestions([]);
    };

    const handleToggleReturnToSameLocation = () => {
        const newValue = !returnToSameLocation;
        setReturnToSameLocation(newValue);

        if (newValue && searchValues.pickup) {
            setSearchValues((prevValues: any) => ({
                ...prevValues,
                return: searchValues.pickup,
            }));
            setReturnLocation(pickupLocation);
            setFieldErrors((prev) => ({ ...prev, returnLocation: undefined }));
        } else if (!newValue) {
            setSearchValues((prevValues: any) => ({
                ...prevValues,
                return: '',
            }));
            setReturnLocation({ latitude: null, longitude: null });
        }
    };

    // --- Search Submission & Results ---
    const validateSearchForm = () => {
        const errors: FieldErrors = {};

        if (!pickupDate) errors.pickupDate = "Pick up date is required";
        if (!dropOffDate) errors.dropOffDate = "Drop off date is required";
        if (!selectedTime) errors.pickupTime = "Pick up time is required";
        if (!selectedDropTime) errors.dropOffTime = "Drop off time is required";
        if (!pickupLocation.latitude || !pickupLocation.longitude) errors.pickupLocation = "Pick up location is required";
        if (!returnLocation.latitude || !returnLocation.longitude) errors.returnLocation = "Return location is required";

        setFieldErrors(errors);

        if (pickupTimeError || dropOffTimeError) {
            return false;
        }

        return Object.keys(errors).length === 0 && !isSameDate;
    };

    const handleSubmit = (isAutoLoad = false) => {
        if (!isAutoLoad) {
            handleTrackButtonClick("Search_Submit");
        }
        if (!validateSearchForm()) {
            return;
        }

        const urlProductIdParams = [
            ...searchParams.getAll("product_id"),
            ...searchParams.getAll("product_ids"),
        ];
        const urlProductIds = urlProductIdParams
            .flatMap((id) => id.split(","))
            .map((id) => id.trim())
            .filter(Boolean);

        let seatCapacity = null;
        let carType = null;
        let amountRange = 0;
        let modelYear = null;
        let Mileage = 0;

        if (customizeResult) {
            const { seat_capacity, car_type, amount_range, mileage, model_year } = customizeResult;
            seatCapacity = seat_capacity;
            carType = car_type;
            amountRange = amount_range;
            modelYear = model_year;
            Mileage = Number(mileage);
        }

        const convertToString = (value: number | null) => value !== null ? value.toString() : "";

        const userData: ProductSearch = {
            index: 1,
            count: 10,
            sort_order: "ASC",
            is_landing_page: true,
            pickup_location: {
                latitude: convertToString(pickupLocation.latitude),
                longitude: convertToString(pickupLocation.longitude),
            },
            return_location: {
                latitude: convertToString(returnLocation.latitude),
                longitude: convertToString(returnLocation.longitude),
            },
            pickup_date: moment(pickupDate).format("YYYY-MM-DDT00:00:00.000") + "Z",
            dropOff_date: moment(dropOffDate).format("YYYY-MM-DDT00:00:00.000") + "Z",
            pickup_time: selectedTime,
            dropOff_time: selectedDropTime,
            ...(seatCapacity && { seat_capacity: seatCapacity }),
            ...(carType && { car_type: carType }),
            ...(amountRange !== 0 && { amount_range: amountRange.toFixed(1) }),
            ...(Mileage !== 0 && { mileage: Mileage.toFixed(1) }),
            ...(modelYear && { model_year: modelYear }),
            ...(brandSearchQuery.trim() && { product_name: brandSearchQuery.trim() }),
            isAutoLoad,
            ...(utmParams.campaign_id && { campaign_code: utmParams.campaign_id }),
        };

        if (!isAutoLoad) {
            ReactPixel.track("Search", {
                content_type: "car_search",
                content_name: "Car Search",
                pickup_location: searchValues.pickup,
                return_location: searchValues.return,
                pickup_date: pickupDate,
                dropoff_date: dropOffDate,
                pickup_time: selectedTime,
                dropoff_time: selectedDropTime,
            });
            TagManager.dataLayer({
                dataLayer: {
                    event: "search_submit",
                    page_type: "home_page",
                    page_path: window.location.pathname,
                    page_url: window.location.href,
                    search_source: "home_page",
                    pickup_location: searchValues.pickup,
                    pickup_datetime: `${pickupDate} ${selectedTime}`,
                    dropoff_datetime: `${dropOffDate} ${selectedDropTime}`,
                }
            });
        }

        handleSearch.mutate(userData);
        setSearchMessage(null);
        setFieldErrors({});
    };

    const handleSearch = useMutation({
        mutationFn: (userData: ProductSearch) =>
            axiosInstance.post(API_URL.PRODUCT_SEARCH, userData),
        onSuccess: (successResponse, variables) => {
            const searchResultData = successResponse.data;
            SetSearchCars(searchResultData);
            setSearchMessage(searchResultData[0]?.message || null);
            setSearchResultHeading(true);

            // Allow DOM to render and layout before smoothly scrolling
            setTimeout(() => {
                const target = document.getElementById("searchResultSection");
                target?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 250);
        },
        onError: (error) => {
            console.error("Search error:", error);
        },
    });

    const normalizeFilterValue = (value: any) => String(value ?? "").trim().toUpperCase();

    const filterCatalog = useMemo(() => {
        const cars = Array.isArray(searchCars) ? searchCars : [];
        const priceValues = cars
            .map((car: any) => Number(car?.product_details_car?.price_per_day))
            .filter((price: number) => Number.isFinite(price) && price > 0);

        const weeklyPriceValues = cars
            .map((car: any) => Number(car?.product_details_car?.price_per_week))
            .filter((price: number) => Number.isFinite(price) && price > 0);

        const monthlyPriceValues = cars
            .map((car: any) => Number(car?.product_details_car?.price_per_month))
            .filter((price: number) => Number.isFinite(price) && price > 0);

        const uniqueSorted = (values: string[]) =>
            Array.from(new Set(values.filter(Boolean).map(v => v.toUpperCase()))).sort((a, b) =>
                a.localeCompare(b),
            );

        return {
            minPrice: priceValues.length ? Math.min(...priceValues) : 0,
            maxPrice: priceValues.length ? Math.max(...priceValues) : 0,
            minWeeklyPrice: weeklyPriceValues.length ? Math.min(...weeklyPriceValues) : 0,
            maxWeeklyPrice: weeklyPriceValues.length ? Math.max(...weeklyPriceValues) : 0,
            minMonthlyPrice: monthlyPriceValues.length ? Math.min(...monthlyPriceValues) : 0,
            maxMonthlyPrice: monthlyPriceValues.length ? Math.max(...monthlyPriceValues) : 0,
            carTypes: uniqueSorted(
                cars.map((car: any) =>
                    car?.product_details_car?.car_type || car?.product_details_car?.category || "",
                ),
            ),
            brands: uniqueSorted(
                cars.map((car: any) =>
                    car?.product_details_car?.car_brand ||
                    car?.brand ||
                    car?.product_name ||
                    "",
                ),
            ),
            fuelTypes: uniqueSorted(
                cars.map((car: any) => car?.product_details_car?.fuel_type || ""),
            ),
            transmissions: uniqueSorted(
                cars.map((car: any) => car?.product_details_car?.transmission || ""),
            ),
        };
    }, [searchCars]);

    const filteredCars = useMemo(() => {
        const cars = Array.isArray(searchCars) ? searchCars : [];

        const minPrice = priceRange.min || 0;
        const maxPrice = priceRange.max || filterCatalog.maxPrice || Number.MAX_SAFE_INTEGER;
        const brandQuery = normalizeFilterValue(brandSearchQuery);

        return cars.filter((car: any) => {
            const price = Number(car?.product_details_car?.price_per_day || 0);
            const weeklyPrice = Number(car?.product_details_car?.price_per_week || 0);
            const monthlyPrice = Number(car?.product_details_car?.price_per_month || 0);
            const carType = normalizeFilterValue(
                car?.product_details_car?.car_type || car?.product_details_car?.category,
            );
            const brand = normalizeFilterValue(
                car?.product_details_car?.car_brand || car?.brand || car?.product_name,
            );
            const fuel = normalizeFilterValue(car?.product_details_car?.fuel_type);
            const transmission = normalizeFilterValue(
                car?.product_details_car?.transmission,
            );

            const priceMatches = price >= minPrice && price <= maxPrice;
            const weeklyPriceMatches =
                !weeklyPriceRange.max ||
                (weeklyPrice >= weeklyPriceRange.min && weeklyPrice <= weeklyPriceRange.max);

            const monthlyPriceMatches =
                !monthlyPriceRange.max ||
                (monthlyPrice >= monthlyPriceRange.min && monthlyPrice <= monthlyPriceRange.max);

            const brandMatches =
                !brandQuery ||
                brand.includes(brandQuery) ||
                normalizeFilterValue(car?.product_name).includes(brandQuery);
            const typeMatches =
                !selectedCarTypes.length ||
                selectedCarTypes.map(normalizeFilterValue).includes(carType);
            const brandFilterMatches =
                !selectedBrands.length ||
                selectedBrands.map(normalizeFilterValue).includes(brand);
            const fuelMatches =
                !selectedFuelTypes.length ||
                selectedFuelTypes.map(normalizeFilterValue).includes(fuel);
            const transmissionMatches =
                !selectedTransmissions.length ||
                selectedTransmissions.map(normalizeFilterValue).includes(transmission);
            const unlimitedMileageMatches =
                !isUnlimitedMileage || !car?.product_details_car?.mileage?.daily_mileage;

            return (
                priceMatches &&
                weeklyPriceMatches &&
                monthlyPriceMatches &&
                brandMatches &&
                typeMatches &&
                brandFilterMatches &&
                fuelMatches &&
                transmissionMatches &&
                unlimitedMileageMatches
            );
        });
    }, [
        brandSearchQuery,
        filterCatalog.maxPrice,
        filterCatalog.maxWeeklyPrice,
        filterCatalog.maxMonthlyPrice,
        priceRange.max,
        priceRange.min,
        weeklyPriceRange.max,
        weeklyPriceRange.min,
        monthlyPriceRange.max,
        monthlyPriceRange.min,
        searchCars,
        selectedBrands,
        selectedCarTypes,
        selectedFuelTypes,
        selectedTransmissions,
        isUnlimitedMileage,
    ]);

    const totalPages = Math.max(1, Math.ceil(filteredCars.length / itemsPerPage));
    const paginatedCars = filteredCars.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
    );

    useEffect(() => {
        setPriceRange({
            min: filterCatalog.minPrice,
            max: filterCatalog.maxPrice,
        });
        setWeeklyPriceRange({
            min: filterCatalog.minWeeklyPrice,
            max: filterCatalog.maxWeeklyPrice,
        });
        setMonthlyPriceRange({
            min: filterCatalog.minMonthlyPrice,
            max: filterCatalog.maxMonthlyPrice,
        });
        setSelectedCarTypes([]);
        setSelectedBrands([]);
        setSelectedFuelTypes([]);
        setSelectedTransmissions([]);
        setIsUnlimitedMileage(false);
        setBrandSearchQuery("");
        setCurrentPage(1);
    }, [filterCatalog.maxPrice, filterCatalog.minPrice, filterCatalog.maxWeeklyPrice, filterCatalog.minWeeklyPrice, filterCatalog.maxMonthlyPrice, filterCatalog.minMonthlyPrice, searchCars]);

    const handleClearSearch = () => {
        handleTrackButtonClick("Clear_All");
        setIsRotating(true);
        setTimeout(() => setIsRotating(false), 800);

        setSearchValues({
            pickup: "",
            return: "",
        });
        setPickupLocation({ latitude: null, longitude: null });
        setReturnLocation({ latitude: null, longitude: null });
        setPickupDate("");
        setDropOffDate("");
        setSelectedTime("");
        setSelectedDropTime("");
        setBrandSearchQuery("");
        setFieldErrors({});
        setPickupTimeError("");
        setDropOffTimeError("");
        setIsSameDate(false);
        setSearchResultHeading(false);
        SetSearchCars([]);
    };

    const resetFilters = () => {
        handleTrackButtonClick("Reset_Filters");
        setIsRotating(true);
        setTimeout(() => setIsRotating(false), 800);

        setPriceRange({
            min: filterCatalog.minPrice,
            max: filterCatalog.maxPrice,
        });
        setWeeklyPriceRange({
            min: filterCatalog.minWeeklyPrice,
            max: filterCatalog.maxWeeklyPrice,
        });
        setMonthlyPriceRange({
            min: filterCatalog.minMonthlyPrice,
            max: filterCatalog.maxMonthlyPrice,
        });
        setSelectedCarTypes([]);
        setSelectedBrands([]);
        setSelectedFuelTypes([]);
        setSelectedTransmissions([]);
        setIsUnlimitedMileage(false);
        setBrandSearchQuery("");
        setCurrentPage(1);
    };

    const handleCustomizeResult = (customResult: any) => {
        setCustomizeResult(customResult);
        if (customResult.priceRange) setPriceRange(customResult.priceRange);
        if (customResult.weeklyPriceRange) setWeeklyPriceRange(customResult.weeklyPriceRange);
        if (customResult.monthlyPriceRange) setMonthlyPriceRange(customResult.monthlyPriceRange);
        if (customResult.selectedCarTypes) setSelectedCarTypes(customResult.selectedCarTypes);
        if (customResult.selectedBrands) setSelectedBrands(customResult.selectedBrands);
        if (customResult.selectedFuelTypes) setSelectedFuelTypes(customResult.selectedFuelTypes);
        if (customResult.selectedTransmissions) setSelectedTransmissions(customResult.selectedTransmissions);
        if (customResult.isUnlimitedMileage !== undefined) setIsUnlimitedMileage(customResult.isUnlimitedMileage);
    };

    const handleCustomization = () => {
        setShowCreateProduct(true);
    };

    const toggleFilterValue = (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
        const normalizedValue = String(value || "").trim().toUpperCase();
        setter((prev) => {
            const isRemoving = prev.includes(normalizedValue);
            return isRemoving ? prev.filter((item: string) => item !== normalizedValue) : [...prev, normalizedValue];
        });
        setCurrentPage(1);
    };

    const handleDailyPriceChange = (min: number, max: number) => {
        setPriceRange({ min, max });
        setWeeklyPriceRange({
            min: filterCatalog.minWeeklyPrice,
            max: filterCatalog.maxWeeklyPrice,
        });
        setMonthlyPriceRange({
            min: filterCatalog.minMonthlyPrice,
            max: filterCatalog.maxMonthlyPrice,
        });
        setCurrentPage(1);
    };

    const handleWeeklyPriceChange = (min: number, max: number) => {
        setWeeklyPriceRange({ min, max });
        setPriceRange({
            min: filterCatalog.minPrice,
            max: filterCatalog.maxPrice,
        });
        setMonthlyPriceRange({
            min: filterCatalog.minMonthlyPrice,
            max: filterCatalog.maxMonthlyPrice,
        });
        setCurrentPage(1);
    };

    const handleMonthlyPriceChange = (min: number, max: number) => {
        setMonthlyPriceRange({ min, max });
        setPriceRange({
            min: filterCatalog.minPrice,
            max: filterCatalog.maxPrice,
        });
        setWeeklyPriceRange({
            min: filterCatalog.minWeeklyPrice,
            max: filterCatalog.maxWeeklyPrice,
        });
        setCurrentPage(1);
    };

    // --- Booking Execution Flow ---
    const handleBookingClick = (item?: any) => {
        handleTrackButtonClick("Book_Now");
        if (item) {
            setSelectedBookingItem(item);

            const payload: any = {
                product_id: item?.product_id,
                base_product_id: item?.product_id,
                product_name: item?.product_name,
                product_description: item?.product_description,
                merchant_id: item?.merchant_id,
                quantity: 1,
                item_id: "",
                promotion_id: "",
                price: item?.product_details_car?.price_per_day,
                price_per_week: item?.product_details_car?.price_per_week,
                price_per_month: item?.product_details_car?.price_per_month,
                toppings: [],
                buy_more_pay_less: {},
                discount_event: {},
                variant: {},
                shop_id: item?.vendor_product_inventories?.[0]?.vendor_id || "",
                product_details: item?.product_details?.description,
            };

            if (process.env.REACT_APP_META_PIXEL_ID) {
                try {
                    if (!(window as any).fbq) {
                        ReactPixel.init(process.env.REACT_APP_META_PIXEL_ID);
                    }
                    ReactPixel.track("AddToCart", {
                        content_type: "Product",
                        content_name: payload?.product_name,
                        content_category: "Add to Cart",
                        content_ids: [payload?.product_id],
                    });
                } catch (_) { }
            }

            try {
                TagManager.dataLayer({
                    dataLayer: {
                        event: "add_to_cart",
                        page_type: "standard",
                        page_path: window.location.pathname,
                        page_url: window.location.href,
                        product_id: payload?.product_id,
                        product_name: payload?.product_name
                    }
                });
            } catch (_) { }

            trackEvent({
                event_name: {
                    web: "Kirkos_Homepage_Add_To_Cart",
                    app: "Kirkos_Homepage_Add_To_Cart",
                },
                event_category: "Homepage_Info",
                module: "HomePage",
                payload: payload,
            }).catch(() => { });
        }

        setAcceptConfirmationMessage("Login to continue on this website");
        setShowAcceptConfirmationModal(true);
        // const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
        // if (token) {
        //     const pendingSearchData = {
        //         pickupPlace: searchMessage ? "Dubai Marina, Dubai, United Arab Emirates" : searchValues.pickup,
        //         returnPlace: searchMessage ? "Dubai Marina, Dubai, United Arab Emirates" : searchValues.return,
        //         returnToSameLocation,
        //         pickupDate: pickupDate ? new Date(pickupDate) : pickupDate,
        //         dropOffDate: dropOffDate ? new Date(dropOffDate) : dropOffDate,
        //         selectedTime,
        //         selectedDropTime,
        //         pickupLocLatitude: searchMessage ? "25.0786415" : pickupLocation?.latitude?.toString(),
        //         pickupLocLongitude: searchMessage ? "55.1352524" : pickupLocation?.longitude?.toString(),
        //         returnLocLatitude: searchMessage ? "25.0786415" : returnLocation?.latitude?.toString(),
        //         returnLocLongitude: searchMessage ? "55.1352524" : returnLocation?.longitude?.toString(),
        //         customizeResult,
        //     };

        //     const bookingState = {
        //         selectedItem: item,
        //         merchantId: item.merchant_id,
        //         searchData: pendingSearchData,
        //         pickupCharge: item?.product_car_insurance?.pick_up_charge_outside_40km,
        //         deliveryCharge: item?.product_car_insurance?.delivery_charges_outside_40km,
        //     };

        //     navigate("/customer/unified-booking", {
        //         state: bookingState,
        //     });
        // } else {
        //     setAcceptConfirmationMessage("Login to continue on this website");
        //     setShowAcceptConfirmationModal(true);
        // }
    };

    // --- Restored Original CTA Click Handlers ---
    const handleScrollToCta = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        const target = document.getElementById("meta-cta");
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const handleBookNowClick = (buttonLocation: string) => {
        if (process.env.REACT_APP_META_PIXEL_ID) {
            try {
                ReactPixel.track('BookNow_Click', {
                    content_name: 'Book Now Button',
                    content_category: 'Navigation',
                    content_type: 'Button Click',
                    button_location: buttonLocation,
                    value: 0,
                    currency: 'AED',
                    ...cleanUtmParams,
                });
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'add_to_cart',
                        page_type: 'campaign_landing',
                        page_path: window.location.pathname,
                        page_url: window.location.href,
                        button_location: buttonLocation,
                        ...cleanUtmParams
                    }
                });
            } catch (error) {
                console.error('Meta Pixel tracking failed:', error);
            }
        }
        // Smoothly scroll to the search form so they can start their search immediately!
        const target = searchFormRef.current;
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const handleGetStartedClick = () => {
        if (process.env.REACT_APP_META_PIXEL_ID) {
            try {
                ReactPixel.track('GetStarted_Click', {
                    content_name: 'Get Started Button',
                    content_category: 'Hero Section',
                    content_type: 'Button Click',
                    button_location: 'Hero Section',
                    value: 0,
                    currency: 'AED',
                    ...cleanUtmParams,
                });
            } catch (error) {
                console.error('Meta Pixel tracking failed:', error);
            }
        }
        // Smoothly scroll to search form
        const target = searchFormRef.current;
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const handleSignInClick = () => {
        if (process.env.REACT_APP_META_PIXEL_ID) {
            try {
                ReactPixel.track('SignIn_Click', {
                    content_name: 'Sign In Button',
                    content_category: 'Hero Section',
                    content_type: 'Button Click',
                    button_location: 'Hero Section',
                    value: 0,
                    currency: 'AED',
                    ...cleanUtmParams,
                });
            } catch (error) {
                console.error('Meta Pixel tracking failed:', error);
            }
        }
        navigate("/signin", {
            state: {
                ...cleanUtmParams,
                campaignId: utmParams.campaign_id,
            },
        });
    };

    const handleTrackButtonClick = (buttonName: string) => {
        trackEvent({
            event_name: {
                web: `Kirkos_Meta_${buttonName}_Click`,
                app: `Kirkos_Meta_${buttonName}_Click_App`,
            },
            event_category: "Button_Click",
            module: "Marketing",
            payload: { button_name: buttonName, ...cleanUtmParams },
        }).catch(() => { });
    };

    const handleWhatsAppClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        await trackEvent({
            event_name: { web: "Kirkos_LandingPage_WhatsAppClick", app: "Kirkos_LandingPage_WhatsAppClick_App" },
            event_category: "Contact_Us",
            module: "Marketing",
            payload: { ...cleanUtmParams },
        });

        if (process.env.REACT_APP_META_PIXEL_ID) {
            try {
                ReactPixel.track('WhatsApp_Click', {
                    content_name: 'WhatsApp Button',
                    content_category: 'CTA Section',
                    content_type: 'Button Click',
                    button_location: 'Final CTA',
                    value: 0,
                    currency: 'AED',
                    ...cleanUtmParams,
                });
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'whatsapp_click',
                        page_type: 'campaign_landing',
                        page_path: window.location.pathname,
                        page_url: window.location.href,
                        ...cleanUtmParams
                    }
                });
            } catch (error) {
                console.error('Meta Pixel tracking failed:', error);
            }
        }

        const cleanNumber = WHATSAPP_NUMBER.replace(/\+/g, "");
        const mobileWhatsApp = `https://wa.me/${cleanNumber}`;
        const webWhatsApp = `https://web.whatsapp.com/send?phone=${cleanNumber}`;
        const desktopWhatsApp = `whatsapp://send?phone=${cleanNumber}`;

        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

        const focusExistingWhatsAppTab = () => {
            const whatsappTab = window.open("", "whatsapp");
            if (
                whatsappTab &&
                !whatsappTab.closed &&
                whatsappTab.location.href.includes("web.whatsapp.com")
            ) {
                whatsappTab.focus();
                return true;
            }
            return false;
        };

        if (isMobile) {
            window.location.href = mobileWhatsApp;
        } else {
            let iframe: HTMLIFrameElement | null = document.createElement("iframe");
            iframe.style.display = "none";
            document.body.appendChild(iframe);

            const handleBlur = () => {
                if (iframe && document.body.contains(iframe)) {
                    clearTimeout(desktopAppTimeout);
                    document.body.removeChild(iframe);
                    iframe = null;
                }
                window.removeEventListener("blur", handleBlur);
                window.removeEventListener("focus", handleFocus);
            };

            const handleFocus = () => {
                setTimeout(() => {
                    if (iframe && document.body.contains(iframe)) {
                        clearTimeout(desktopAppTimeout);
                        document.body.removeChild(iframe);
                        iframe = null;
                        if (!focusExistingWhatsAppTab()) {
                            window.open(webWhatsApp, "whatsapp");
                        }
                    }
                }, 500);
            };

            const desktopAppTimeout = setTimeout(() => {
                if (iframe && document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                    iframe = null;
                    if (!focusExistingWhatsAppTab()) {
                        window.open(webWhatsApp, "whatsapp");
                    }
                }
            }, 1500);

            window.addEventListener("blur", handleBlur);
            window.addEventListener("focus", handleFocus);

            iframe.src = desktopWhatsApp;
        }
    };

    return (
        <div className="kirkos-meta-wrapper">
            <SEO
                title="Kirkos UAE | Your Premium Car Rental Solution"
                description="Book your next journey with Kirkos UAE. Premium car rentals in Dubai and Sharjah with transparent pricing and instant booking."
                keywords="UAE car rental, Dubai car hire, Sharjah car rental, luxury car rental UAE, Kirkos"
            />
            <header className="kirkos-meta-hero">


                <div className="kirkos-meta-hero-content">
                    <div className="kirkos-meta-hero-text">
                        <p className="kirkos-meta-tag">EXCLUSIVE CAMPAIGN OFFER</p>
                        <h1>
                            Unlock Special Campaign Rates on Premium Car Rentals in the UAE
                        </h1>
                        <p className="kirkos-meta-subtitle">
                            Enjoy exclusive, limited-time promotional pricing on UAE's finest car fleet.
                            Hand-picked models are available for selected campaign location with zero hidden fees, flexible options, and full coverage.
                        </p>

                        {/* ── Search Form Embedded Seamlessly Inside Hero Text ── */}
                        <div ref={searchFormRef} id="searchFormSection" className="landing-shell p-0 bg-transparent border-0 text-start mt-4 mb-4" style={{ scrollMarginTop: "120px", width: "100%" }}>
                            <div className="landing-shell__search-body mx-auto" style={{ maxWidth: "1000px" }}>

                                {/* ── Exclusive Campaign Badge ── */}
                                <div className="landing-shell__prefs-row mb-3">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="landing-shell__prefs-info" style={{ background: "rgba(255, 193, 7, 0.15)", color: "#ffc107", padding: "6px 14px", borderRadius: "20px", fontSize: "11px", fontWeight: "600", letterSpacing: "0.5px" }}>
                                            <TagFill className="me-1" size={11} />
                                            CAMPAIGN SPECIAL OFFER
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className={`landing-shell__toggle ${returnToSameLocation ? "is-active" : ""}`}
                                        onClick={handleToggleReturnToSameLocation}
                                    >
                                        {returnToSameLocation ? <Check2 size={14} className="me-1" /> : null}
                                        Return to same location
                                    </button>
                                </div>

                                {/* ── Row 2: Location pickers ── */}
                                <div className="row g-3 mb-3">
                                    <div className={returnToSameLocation ? "col-12" : "col-12 col-lg-6"}>
                                        <div className="position-relative">
                                            <div onClick={() => setShowPickupMapModal(true)} style={{ cursor: "pointer" }}>
                                                <FloatingInputLocation
                                                    inputType={"text"}
                                                    className={""}
                                                    placeholder={"Pick Up Location"}
                                                    label={"PICK UP LOCATION"}
                                                    inputName="pickUpLocation"
                                                    selectedLocationName={searchValues.pickup}
                                                    onInputChange={(value: string) => handlePickupLocationChange(value)}
                                                    control={control}
                                                    editable={false}
                                                />
                                            </div>
                                            {fieldErrors.pickupLocation && (
                                                <div className="text-danger mt-1" style={{ fontSize: "12px", fontWeight: "500" }}>
                                                    {fieldErrors.pickupLocation}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {!returnToSameLocation && (
                                        <div className="col-12 col-lg-6">
                                            <div className="position-relative">
                                                <div onClick={() => setShowReturnMapModal(true)} style={{ cursor: "pointer" }}>
                                                    <FloatingInputLocation
                                                        inputType={"text"}
                                                        className={""}
                                                        placeholder={"Return Location"}
                                                        label={"RETURN LOCATION"}
                                                        inputName="returnLocation"
                                                        onInputChange={(value: string) => handleReturnLocationChange(value)}
                                                        selectedLocationName={searchValues.return}
                                                        control={control}
                                                        editable={false}
                                                    />
                                                </div>
                                                {fieldErrors.returnLocation && (
                                                    <div className="text-danger mt-1" style={{ fontSize: "12px", fontWeight: "500" }}>
                                                        {fieldErrors.returnLocation}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── Row 3: Date/Time + Brand + Search ── */}
                                <div className="row g-3 align-items-end">
                                    {/* Pick-up Date & Time */}
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <DateTimePickerComponent
                                            label="Pick up Date & Time"
                                            placeholder="Select date & time"
                                            dateValue={pickupDate}
                                            timeValue={selectedTime}
                                            onDateChange={handlePickupDateChange}
                                            onTimeChange={handleTimeChange}
                                            className="w-100"
                                            maxDate={dropOffDate ? new Date(dropOffDate) : null}
                                            maxTime={pickupDate && dropOffDate && pickupDate === dropOffDate ? selectedDropTime : null}
                                            minDate={(() => {
                                                const today = new Date();
                                                const currentHour = today.getHours();
                                                const currentMinutes = today.getMinutes();
                                                const isNoServiceToday = (currentHour * 60 + currentMinutes + 120 >= 22 * 60);
                                                if (isNoServiceToday) {
                                                    const tom = new Date();
                                                    tom.setDate(tom.getDate() + 1);
                                                    return tom;
                                                }
                                                return today;
                                            })()}
                                            minTime={(() => {
                                                // Only restrict the minimum time when the selected pickup date is TODAY.
                                                // For tomorrow or any future date, all times are valid → return null.
                                                if (!pickupDate) return null;
                                                const today = new Date();
                                                const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
                                                if (pickupDate !== todayStr) return null;
                                                const minTimeDate = new Date(today.getTime() + 2 * 60 * 60 * 1000);
                                                const h = String(minTimeDate.getHours()).padStart(2, "0");
                                                const m = String(minTimeDate.getMinutes()).padStart(2, "0");
                                                return `${h}:${m}`;
                                            })()}
                                            showTodayPickupMessage={!!pickupDate && pickupDate === new Date().toISOString().split("T")[0]}
                                            autoScrollToFirstAvailableTime={true}
                                        />
                                    </div>

                                    {/* Drop-off Date & Time */}
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <DateTimePickerComponent
                                            label="Dropoff Date & Time"
                                            placeholder="Select date & time"
                                            dateValue={dropOffDate}
                                            timeValue={selectedDropTime}
                                            onDateChange={handleDropOffDateChange}
                                            onTimeChange={handleDropOffTimeChange}
                                            className="w-100"
                                            minDate={pickupDate ? new Date(pickupDate) : null}
                                            minTime={pickupDate && dropOffDate && pickupDate === dropOffDate ? selectedTime : null}
                                            maxDate={pickupDate ? (() => { const d = new Date(pickupDate); d.setDate(d.getDate() + 30); return d; })() : null}
                                        />
                                    </div>

                                    {/* Brand Name */}
                                    <div className="col-12 col-sm-8 col-lg-4 text-start">
                                        <label htmlFor="homeCarSearch" className="landing-shell__label mb-1 d-block" style={{ fontSize: "0.85rem" }}>
                                            Brand Name
                                        </label>
                                        <input
                                            type="text"
                                            id="homeCarSearch"
                                            className="landing-shell__brand-input w-100 px-3"
                                            placeholder="Brand (optional)"
                                            style={{ height: "48px" }}
                                            value={brandSearchQuery}
                                            onChange={(e) => setBrandSearchQuery(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {(pickupTimeError || dropOffTimeError || fieldErrors.pickupDate || fieldErrors.dropOffDate || fieldErrors.pickupTime || fieldErrors.dropOffTime) && (
                                    <div className="landing-shell__error mt-4">
                                        <div className="d-flex align-items-center gap-2">
                                            <Tools size={16} />
                                            <span>{pickupTimeError || dropOffTimeError || fieldErrors.pickupDate || fieldErrors.dropOffDate || fieldErrors.pickupTime || fieldErrors.dropOffTime}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="row justify-content-end mt-2 align-items-end">
                                    <div className="col-12 col-sm-4 col-lg-2 d-flex align-items-center justify-content-end">
                                        <button
                                            type="button"
                                            className="landing-shell__clear-btn d-flex align-items-center gap-1"
                                            onClick={handleClearSearch}
                                            style={{
                                                background: "transparent",
                                                color: "#fff",
                                                fontSize: "12px",
                                                padding: "4px 0",
                                                transition: "all 0.2s",
                                                opacity: 0.8,
                                                fontWeight: "500",
                                            }}
                                            onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
                                            onMouseOut={(e) => (e.currentTarget.style.opacity = "0.8")}
                                        >
                                            <ArrowClockwise size={14} className={isRotating ? "is-rotating" : ""} />
                                            Clear All
                                        </button>
                                    </div>
                                    {/* Search button */}
                                    <div className="col-12 col-sm-4 col-lg-3">
                                        <button
                                            className="landing-shell__search-button w-100"
                                            onClick={() => handleSubmit()}
                                            style={{
                                                background: "linear-gradient(135deg, #f1c260 0%, #d8a845 100%)",
                                                color: "#000",
                                                border: "none",
                                                borderRadius: "14px",
                                                padding: "14px 0",
                                                fontWeight: "700",
                                                fontSize: "1.05rem",
                                                boxShadow: "0 8px 20px rgba(244, 182, 82, 0.3)",
                                                transition: "all 0.3s ease",
                                            }}
                                            onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 25px rgba(244, 182, 82, 0.4)"; }}
                                            onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(244, 182, 82, 0.3)"; }}
                                        >
                                            <Search size={18} className="me-2" style={{ strokeWidth: "1" }} />
                                            SEARCH VEHICLES
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="kirkos-meta-hero-actions">
                            <button
                                className="kirkos-meta-button primary"
                                onClick={handleGetStartedClick}
                            >
                                Get started
                            </button>
                            <button
                                className="kirkos-meta-button ghost"
                                onClick={handleSignInClick}
                            >
                                I have an account
                            </button>
                        </div>

                    </div>

                </div>
            </header>

            {/* ── Search Results Catalog Section ── */}
            {isSameDate && (
                <div className="landing-shell__inline-error my-3 text-center" style={{ color: "#ff4d4d", fontSize: "0.95rem" }}>
                    Drop-off date must be later than Pick-up date. Please select a valid date.
                </div>
            )}

            <div id="searchResultSection" style={{ scrollMarginTop: "120px" }} />
            <div
                className={`search-list-home home-search-min-height align-items-start ${searchResultHeading ? "" : "d-none"
                    }`}
                style={{ padding: "40px 0", background: "#0c0c0e" }}
            >
                <div ref={resultsShellRef} className="home-results-shell col-12 col-xxl-11 mx-auto px-3 px-xl-4">
                    <main className="home-results-main">
                        <div className="home-results-summary">
                            <div className="home-results-summary__copy w-100">
                                <div className="home-results-summary__eyebrow">
                                    Exclusive car collections
                                </div>
                                <h2 className="text-white">
                                    {searchMessage
                                        ? `No campaign cars found in ${searchValues.pickup.split(",")[0]}. Showing results in Dubai Marina.`
                                        : searchValues.pickup === "Dubai Marina, Dubai, United Arab Emirates"
                                            ? "Exclusive car collections in Dubai Marina"
                                            : `Available cars in ${searchValues.pickup.split(",")[0]}`}
                                </h2>
                                <div className="home-results-summary__meta d-flex align-items-center justify-content-center gap-2">
                                    <Check2 className="text-warning" size={16} />
                                    <span className="text-white-50">{filteredCars.length} premium cars found for your dates</span>
                                </div>
                            </div>

                            {/* Applied Filters Desktop List */}
                            {/* <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                                <button type="button" className="home-results-reset-slim d-flex align-items-center gap-1" onClick={resetFilters}>
                                    <ArrowClockwise size={12} className={isRotating ? "is-rotating" : ""} />
                                    Reset All
                                </button>
                            </div> */}
                        </div>

                        {handleSearch.isPending ? (
                            <div className="home-results-empty text-center py-5">
                                <div className="spinner-border text-warning" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : filteredCars.length === 0 ? (
                            <div className="home-results-empty text-center py-5">
                                <p className="text-white-50">No cars match the current filters.</p>
                            </div>
                        ) : (
                            <>
                                <div className="home-results-grid">
                                    {paginatedCars.map((item: any, index: number) => (
                                        <div key={`${item.product_id || index}`} className="home-result-card-wrap">
                                            <div className="card card-custom-my-account scale-down home-result-card">
                                                <div
                                                    className="position-relative"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setSelectedProduct(item);
                                                        setShowDetailsModal(true);
                                                    }}>
                                                    {item?.product_details?.images?.length > 0 ? (
                                                        <img
                                                            className="img-fluid search-result-image home-result-card__image"
                                                            src={item.product_details.images[0]}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <img
                                                            src={noun_no_image}
                                                            alt=""
                                                            className="img-fluid search-result-image home-result-card__image"
                                                        />
                                                    )}
                                                    {/* Glassmorphic overlay for tags */}
                                                    {item?.tags && Array.isArray(item.tags) && item.tags.length > 0 && (
                                                        <div
                                                            className="position-absolute d-flex flex-wrap gap-1"
                                                            style={{
                                                                top: "10px",
                                                                left: "10px",
                                                                zIndex: 10,
                                                                pointerEvents: "none"
                                                            }}
                                                        >
                                                            {item.tags.map((tag: string, tagIdx: number) => {
                                                                const tagLower = tag.toLowerCase();
                                                                let style: React.CSSProperties = {
                                                                    fontSize: "0.6rem",
                                                                    fontFamily: "Poppins, sans-serif",
                                                                    fontWeight: 800,
                                                                    textTransform: "uppercase",
                                                                    letterSpacing: "0.7px",
                                                                    padding: "0.2rem 0.6rem",
                                                                    borderRadius: "6px",
                                                                    backdropFilter: "blur(6px)",
                                                                    lineHeight: 1.5,
                                                                    whiteSpace: "nowrap",
                                                                    // default (fallback)
                                                                    background: "rgba(18, 21, 28, 0.85)",
                                                                    color: "#f0b64f",
                                                                    border: "1px solid rgba(240, 182, 79, 0.5)",
                                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                                                };

                                                                return (
                                                                    <span
                                                                        key={`${tag}-${tagIdx}`}
                                                                        style={style}
                                                                    >
                                                                        {tag}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="card-body home-result-card__body">
                                                    <div className="row text-start">
                                                        <div className="col-6">
                                                            <div className="car-cat-search home-result-card__brand" title={item?.product_name}>
                                                                {initCapAllWords(item.product_name)}
                                                            </div>
                                                            <div className="car-name-search home-result-card__name" title={item?.product_name}>
                                                                {initCapAllWords(item.product_name)}
                                                            </div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-center merchant-name-div mx-2 mb-1 home-result-card__merchant-box">
                                                                <span className="home-result-card__merchant">
                                                                    {item?.site_logo ? (
                                                                        <img className="img-fluid logo-merchant" src={item?.site_logo} alt="" />
                                                                    ) : (
                                                                        <span title={item?.shop_name}>
                                                                            {String(item?.shop_name).length > 10
                                                                                ? `${String(item?.shop_name).substring(0, 10).toUpperCase()}...`
                                                                                : String(item?.shop_name).toUpperCase()}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <div className="text-center mx-2">
                                                                <div className="merchant-name-container font-size-home-12 home-result-card__merchant-name" title={item?.shop_name}>
                                                                    {item?.shop_name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row pt-2 search-features align-items-center home-result-card__features-row">
                                                        <div className="col-6">
                                                            <div className="col-md-12 text-start pb-2 dashboard-poppins-font-style car-name-search-bold">
                                                                Features
                                                            </div>
                                                            <div className="d-flex">
                                                                <div className="text-start dashboard-poppins-font-style me-2 home-result-card__feature-item">
                                                                    <img src={group_icon} alt="" className="img-fluid img-width-icon" />
                                                                    &nbsp;{item.product_details_car.number_of_seats}
                                                                </div>
                                                                <div className="text-start dashboard-poppins-font-style home-result-card__feature-item">
                                                                    <img src={petrol_pump} alt="" className="img-fluid img-width-icon" />
                                                                    &nbsp;{item.product_details_car.fuel_type}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="search-car-special-deal text-center home-result-card__badge">
                                                                <img src={dashboard_special_deal} alt="" width={13} />
                                                                <span className="dashboard-poppins-font-style"> Special Deal </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row text-start pt-2 home-result-card__pricing-row">
                                                        <div className="col-4">
                                                            <div className="font-size-home-14 dashboard-poppins-font-style home-result-card__price">
                                                                AED <span>{item.product_details_car.price_per_day}</span>
                                                            </div>
                                                            <div className="car-name-search-bold dashboard-poppins-font-style home-result-card__period">
                                                                Per Day
                                                            </div>
                                                            <div className="car-name-search-bold dashboard-poppins-font-style home-result-card__mileage">
                                                                {item?.product_details_car?.mileage?.daily_mileage ? (
                                                                    `${item.product_details_car.mileage.daily_mileage} KM`
                                                                ) : (
                                                                    <div className="car-name-search dashboard-poppins-font-style">
                                                                        Unlimited mileage
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="font-size-home-14 dashboard-poppins-font-style home-result-card__price">
                                                                AED <span>{item.product_details_car.price_per_week}</span>
                                                            </div>
                                                            <div className="car-name-search dashboard-poppins-font-style home-result-card__period">
                                                                Per Week
                                                            </div>
                                                            <div className="car-name-search-bold dashboard-poppins-font-style home-result-card__mileage">
                                                                {item?.product_details_car?.mileage?.weekly_mileage ? (
                                                                    `${item.product_details_car.mileage.weekly_mileage} KM`
                                                                ) : (
                                                                    <div className="car-name-search dashboard-poppins-font-style">
                                                                        Unlimited mileage
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="font-size-home-14 dashboard-poppins-font-style home-result-card__price">
                                                                AED <span>{item.product_details_car.price_per_month}</span>
                                                            </div>
                                                            <div className="car-name-search dashboard-poppins-font-style home-result-card__period">
                                                                Per Month
                                                            </div>
                                                            <div className="car-name-search-bold dashboard-poppins-font-style home-result-card__mileage">
                                                                {item?.product_details_car?.mileage?.monthly_mileage ? (
                                                                    `${item.product_details_car.mileage.monthly_mileage} KM`
                                                                ) : (
                                                                    <div className="car-name-search dashboard-poppins-font-style">
                                                                        Unlimited mileage
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row pt-2">
                                                        <div className="col-md-12 d-flex justify-content-between align-items-center">
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setSelectedProduct(item);
                                                                    setShowDetailsModal(true);
                                                                }}
                                                                className="home-result-card__details-link"
                                                            >
                                                                VIEW DETAILS
                                                            </a>
                                                            <button
                                                                className="book-now-button-style home-result-card__book"
                                                                onClick={() => handleBookingClick(item)}
                                                            >
                                                                BOOK NOW
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="home-results-pagination mt-4">
                                    <button
                                        type="button"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <span>
                                        {currentPage} / {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={currentPage === totalPages}
                                        onClick={() =>
                                            setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                                        }
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </>
                        )}
                    </main>
                </div>
            </div>

            {/* <section className="kirkos-meta-highlight">
                <div className="kirkos-meta-highlight-grid">
                    {highlights.map((item) => (
                        <div key={item.title} className="kirkos-meta-highlight-card">
                            <div className="kirkos-meta-icon">
                                <img src={item.icon} alt={item.title} />
                            </div>
                            <h3>{item.title}</h3>
                            <p>{item.description}</p>
                        </div>
                    ))}
                </div>
            </section> */}

            <div className="kirkos-meta-stats-container">
                <div className="kirkos-meta-stats">
                    {metaStats.map((stat, idx) => {
                        const Icon = idx === 0 ? StarFill : idx === 1 ? CarFront : Clock;
                        return (
                            <div key={stat.label} className="kirkos-meta-stat">
                                <div className="kirkos-meta-stat-icon-wrapper">
                                    <Icon className="text-warning" size={22} />
                                </div>
                                <div className="kirkos-meta-stat-info">
                                    <span>{stat.value}</span>
                                    <p>{stat.label}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <section className="kirkos-meta-section">
                <div className="kirkos-meta-section-header">
                    <h2>Why renters choose KIRKOS</h2>
                    <p>
                        Built to simplify your booking journey and deliver the best driving
                        experience across the UAE.
                    </p>
                </div>
                <div className="kirkos-meta-feature-tiles">
                    {featureTiles.map((tile) => (
                        <div key={tile.title} className="kirkos-meta-feature-tile">
                            <div className="kirkos-meta-feature-icon-wrapper">
                                <img src={tile.icon} alt={tile.title} />
                            </div>
                            <div className="kirkos-meta-feature-info">
                                <h4>{tile.title}</h4>
                                <p>{tile.value}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="kirkos-meta-steps">
                <div className="kirkos-meta-section-header">
                    <h2>3-step booking flow</h2>
                    <p>Everything you need to get on the road quickly.</p>
                </div>
                <div className="kirkos-meta-steps-grid">
                    {steps.map((step, index) => (
                        <div key={step.title} className="kirkos-meta-step-card">
                            <span className="kirkos-meta-step-count">
                                {String(index + 1).padStart(2, "0")}
                            </span>
                            <h3>{step.title}</h3>
                            <p>{step.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="kirkos-meta-offer">
                {claimAsset && (
                    <div className="kirkos-meta-offer-card">
                        <div>
                            <p className="kirkos-meta-tag">
                                {claimAsset.header}
                            </p>
                            <h2>{claimAsset.title}</h2>
                            <p>
                                {claimAsset.description}
                            </p>
                        </div>
                        <a
                            href="#searchFormSection"
                            className="kirkos-meta-button primary"
                            onClick={() => handleBookNowClick('Offer Section')}
                        >
                            {claimAsset.cta}
                        </a>
                    </div>
                )}
            </section>

            <section className="kirkos-meta-section">
                <div className="kirkos-meta-section-header">
                    <h2>Coverage across Dubai and Sharjah</h2>
                    <p>
                        Delivery, airport pickup, and seamless returns wherever you are.
                    </p>
                </div>
                <div className="kirkos-meta-city-grid">
                    {cityTags.map((city) => (
                        <span key={city} className="kirkos-meta-city-tag">
                            {city}
                        </span>
                    ))}
                </div>
            </section>

            <section className="kirkos-meta-cta" id="meta-cta">
                <div className="kirkos-meta-cta-card">
                    <div>
                        <h2>Ready to drive with KIRKOS?</h2>
                        <p>
                            Start your booking in minutes, or speak with a live agent for the
                            best match.
                        </p>
                    </div>
                    <div className="kirkos-meta-cta-actions">
                        <a
                            href="#searchFormSection"
                            className="kirkos-meta-button primary"
                            onClick={() => handleBookNowClick('Final CTA Section')}
                        >
                            Start booking
                        </a>
                        <a
                            href="https://wa.me/+97165674835"
                            className="kirkos-meta-button ghost"
                            onClick={handleWhatsAppClick}
                            id="contact-whatsapp"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Chat on WhatsApp
                        </a>
                    </div>
                </div>
                <img src={home_backdrop} alt="Kirkos brand backdrop" />
            </section>

            {/* --- Advanced Custom Filters Modal --- */}
            <CustomerCustomizeYourSearch
                isOpen={showCreateProduct}
                onClose={() => setShowCreateProduct(false)}
                onCustomizeResult={handleCustomizeResult}
                filterCatalog={filterCatalog}
                totalCars={searchCars}
                initialValues={customizeResult}
            />

            {/* --- Auth Quick Search Confirmation Modal --- */}
            <QuickSearchModal
                isOpen={showAcceptConfirmationModal}
                onClose={() => {
                    setShowAcceptConfirmationModal(false);
                    setSelectedBookingItem(null);
                }}
                onConfirm={() => {
                    setShowAcceptConfirmationModal(false);
                    if (selectedBookingItem) {
                        const pendingSearchData = {
                            pickupPlace: searchMessage ? "Dubai Marina, Dubai, United Arab Emirates" : searchValues.pickup,
                            returnPlace: searchMessage ? "Dubai Marina, Dubai, United Arab Emirates" : searchValues.return,
                            returnToSameLocation,
                            pickupDate: pickupDate ? new Date(pickupDate) : pickupDate,
                            dropOffDate: dropOffDate ? new Date(dropOffDate) : dropOffDate,
                            selectedTime,
                            selectedDropTime,
                            pickupLocLatitude: searchMessage ? "25.0786415" : pickupLocation?.latitude?.toString(),
                            pickupLocLongitude: searchMessage ? "55.1352524" : pickupLocation?.longitude?.toString(),
                            returnLocLatitude: searchMessage ? "25.0786415" : returnLocation?.latitude?.toString(),
                            returnLocLongitude: searchMessage ? "55.1352524" : returnLocation?.longitude?.toString(),
                            customizeResult,
                        };

                        const pendingBooking = {
                            selectedItem: selectedBookingItem,
                            merchantId: selectedBookingItem.merchant_id,
                            searchData: pendingSearchData,
                            pickupCharge: selectedBookingItem?.product_car_insurance?.pick_up_charge_outside_40km,
                            deliveryCharge: selectedBookingItem?.product_car_insurance?.delivery_charges_outside_40km,
                        };

                        sessionStorage.setItem("pendingBookingFromHomepage", "true");
                        sessionStorage.setItem("pendingBookingData", JSON.stringify(pendingBooking));

                        navigate("/signin", {
                            state: {
                                pendingBooking: pendingBooking,
                                ...cleanUtmParams,
                                campaignId: utmParams.campaign_id,
                            },
                        });
                    } else {
                        navigate("/signin", {
                            state: {
                                ...cleanUtmParams,
                                campaignId: utmParams.campaign_id,
                            },
                        });
                    }
                }}
                onRegister={() => {
                    setShowAcceptConfirmationModal(false);
                    if (selectedBookingItem) {
                        const pendingSearchData = {
                            pickupPlace: searchMessage ? "Dubai Marina, Dubai, United Arab Emirates" : searchValues.pickup,
                            returnPlace: searchMessage ? "Dubai Marina, Dubai, United Arab Emirates" : searchValues.return,
                            returnToSameLocation,
                            pickupDate: pickupDate ? new Date(pickupDate) : pickupDate,
                            dropOffDate: dropOffDate ? new Date(dropOffDate) : dropOffDate,
                            selectedTime,
                            selectedDropTime,
                            pickupLocLatitude: searchMessage ? "25.0786415" : pickupLocation?.latitude?.toString(),
                            pickupLocLongitude: searchMessage ? "55.1352524" : pickupLocation?.longitude?.toString(),
                            returnLocLatitude: searchMessage ? "25.0786415" : returnLocation?.latitude?.toString(),
                            returnLocLongitude: searchMessage ? "55.1352524" : returnLocation?.longitude?.toString(),
                            customizeResult,
                        };

                        const pendingBooking = {
                            selectedItem: selectedBookingItem,
                            merchantId: selectedBookingItem.merchant_id,
                            searchData: pendingSearchData,
                            pickupCharge: selectedBookingItem?.product_car_insurance?.pick_up_charge_outside_40km,
                            deliveryCharge: selectedBookingItem?.product_car_insurance?.delivery_charges_outside_40km,
                        };

                        sessionStorage.setItem("pendingBookingFromHomepage", "true");
                        sessionStorage.setItem("pendingBookingData", JSON.stringify(pendingBooking));

                        navigate("/signup", {
                            state: {
                                pendingBooking: pendingBooking,
                                ...cleanUtmParams,
                                campaignId: utmParams.campaign_id,
                            },
                        });
                    } else {
                        navigate("/signup", {
                            state: {
                                ...cleanUtmParams,
                                campaignId: utmParams.campaign_id,
                            },
                        });
                    }
                    setSelectedBookingItem(null);
                }}
                selectedProduct={selectedBookingItem}
                message={acceptConfirmationMessage}
                locationName={searchValues.pickup}
                startDate={pickupDate}
                endDate={dropOffDate}
            />

            {/* --- Vehicle Product Details Modal --- */}
            <Modal
                show={showDetailsModal}
                onHide={() => setShowDetailsModal(false)}
                size="lg"
                centered
                dialogClassName="home-details-modal-dialog"
                contentClassName="home-details-modal-content"
            >
                <Modal.Header closeButton style={{ padding: "0.75rem 1.25rem", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <Modal.Title style={{ fontSize: "1.1rem", fontWeight: "700", color: "#fff" }}>
                        {selectedProduct?.product_name?.toUpperCase() || "Product Details"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto", padding: "1.25rem" }}>
                    {selectedProduct && (
                        <div className="container-fluid">
                            <div className="row" style={{ marginBottom: 0 }}>
                                <div className="col-md-5" style={{ paddingBottom: 0 }}>
                                    {selectedProduct?.product_details?.images?.length > 0 ? (
                                        <img
                                            className="img-fluid rounded"
                                            src={selectedProduct?.product_details?.images?.[0]}
                                            alt={selectedProduct?.product_name}
                                            style={{ width: "100%", maxHeight: "250px", objectFit: "cover" }}
                                        />
                                    ) : (
                                        <img
                                            src={noun_no_image}
                                            alt="No image"
                                            className="img-fluid rounded"
                                            style={{ width: "100%", maxHeight: "250px", objectFit: "cover" }}
                                        />
                                    )}
                                </div>
                                <div className="col-md-7" style={{ paddingBottom: 0 }}>
                                    <div className="mb-3 pb-2 border-bottom">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <div className="fw-bold">{selectedProduct?.shop_name || "N/A"}</div>
                                            </div>
                                            {selectedProduct?.site_logo && (
                                                <div className="text-end">
                                                    <img
                                                        src={selectedProduct.site_logo}
                                                        alt={selectedProduct?.shop_name || "Merchant Logo"}
                                                        style={{ maxHeight: "36px", maxWidth: "90px", objectFit: "contain" }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <h6 className="mb-1" style={{ fontSize: "0.8rem", fontWeight: "600" }}>Vehicle Specifications</h6>
                                        <div className="row g-1" style={{ fontSize: "0.75rem" }}>
                                            {selectedProduct?.product_details_car?.car_type && (
                                                <div className="col-6">
                                                    <strong>Type:</strong> {selectedProduct.product_details_car.car_type}
                                                </div>
                                            )}
                                            {selectedProduct?.product_details_car?.model_year && (
                                                <div className="col-6">
                                                    <strong>Year:</strong> {selectedProduct.product_details_car.model_year}
                                                </div>
                                            )}
                                            {selectedProduct?.product_details_car?.transmission && (
                                                <div className="col-6">
                                                    <strong>Transmission:</strong> {selectedProduct.product_details_car.transmission}
                                                </div>
                                            )}
                                            {selectedProduct?.product_details_car?.fuel_type && (
                                                <div className="col-6">
                                                    <strong>Fuel:</strong> {selectedProduct.product_details_car.fuel_type}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ padding: "0.4rem 0.75rem" }}>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowDetailsModal(false)}
                    >
                        Close
                    </button>
                    <button
                        className="book-now-button-style"
                        onClick={() => {
                            if (selectedProduct) {
                                setShowDetailsModal(false);
                                handleBookingClick(selectedProduct);
                            }
                        }}
                    >
                        BOOK NOW
                    </button>
                </Modal.Footer>
            </Modal>

            {/* --- Location Picker Modals --- */}
            <LocationPickerModal
                isOpen={showPickupMapModal}
                onClose={() => setShowPickupMapModal(false)}
                onLocationSelect={handlePickupMapSelect}
                initialLocation={
                    pickupLocation.latitude !== null && pickupLocation.longitude !== null
                        ? { latitude: pickupLocation.latitude as number, longitude: pickupLocation.longitude as number }
                        : { latitude: 25.1972, longitude: 55.2744 }
                }
                initialAddress={searchValues.pickup}
                title="Select Pick Up Location"
            />
            <LocationPickerModal
                isOpen={showReturnMapModal}
                onClose={() => setShowReturnMapModal(false)}
                onLocationSelect={handleReturnMapSelect}
                initialLocation={
                    returnLocation.latitude !== null && returnLocation.longitude !== null
                        ? { latitude: returnLocation.latitude as number, longitude: returnLocation.longitude as number }
                        : pickupLocation.latitude !== null && pickupLocation.longitude !== null
                            ? { latitude: pickupLocation.latitude as number, longitude: pickupLocation.longitude as number }
                            : { latitude: 25.1972, longitude: 55.2744 }
                }
                initialAddress={searchValues.return}
                title="Select Return Location"
                disableAutoSelectOnMount={!searchValues.return}
            />

            <DraggableWhatsAppIcon />
        </div>
    );
};

export default KirkosMetaLanding;